import React from 'react';
import { Settings } from 'lucide-react';
import { Accordion } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PermissionCard, SectionAccordionItem } from '../cards';
import { BulkActionsDropdown } from '../BulkActionsDropdown';
import { AdministrationSubSettings, type AdminSubSettingItem } from './AdministrationSubSettings';

export interface AdministrationTabProps {
  manageGeneralSettings: string;
  onManageGeneralSettingsChange: (value: 'view' | 'configure') => void;
  subSettings: AdminSubSettingItem[];
  onSubSettingChange: (item: AdminSubSettingItem, value: boolean) => void;
  manageRolesPermissions: boolean;
  onManageRolesPermissionsChange: (value: boolean) => void;
  manageRates: boolean;
  onManageRatesChange: (value: boolean) => void;
  isAdministrator: boolean;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export const AdministrationTab: React.FC<AdministrationTabProps> = ({
  manageGeneralSettings,
  onManageGeneralSettingsChange,
  subSettings,
  onSubSettingChange,
  manageRolesPermissions,
  onManageRolesPermissionsChange,
  manageRates,
  onManageRatesChange,
  isAdministrator,
  onSelectAll,
  onDeselectAll,
}) => {
  const viewOnly = manageGeneralSettings === 'view';

  return (
    <>
      <BulkActionsDropdown onSelectAll={onSelectAll} onDeselectAll={onDeselectAll} />
      <Accordion type="multiple" defaultValue={['system-settings']} className="space-y-3">
        <SectionAccordionItem
          value="system-settings"
          icon={<Settings className="h-5 w-5" />}
          title="System Settings"
          description="Control access to system-wide configuration and administrative functions"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:auto-rows-fr gap-3">

            {/* General Settings — half-width grid cell (pairs with the sub-settings) */}
            <div className="h-full">
              <PermissionCard
                label={<Label htmlFor="manage-general-settings">Manage General Settings</Label>}
                description="View or modify core system configuration including company details, branding, and default preferences"
                control={
                  <Select value={manageGeneralSettings} onValueChange={onManageGeneralSettingsChange}>
                    <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="view">View Only</SelectItem>
                      <SelectItem value="configure">Configure</SelectItem>
                    </SelectContent>
                  </Select>
                }
              />
            </div>

            {/* All sub-settings require Configure access */}
            <AdministrationSubSettings items={subSettings} disabled={viewOnly} onChange={onSubSettingChange} />

            {/* Manage Roles & Permissions — special case: locked for system roles */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="h-full">
                    <PermissionCard
                      label={
                        <Label
                          htmlFor="manage-roles-permissions"
                          className={(viewOnly || isAdministrator) ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}
                        >
                          Manage Roles & Permissions
                        </Label>
                      }
                      description="Create custom user roles, assign permission levels, and control what different user types can access and modify"
                      dimmed={viewOnly || isAdministrator}
                      control={
                        <Checkbox
                          id="manage-roles-permissions"
                          checked={isAdministrator ? true : manageRolesPermissions}
                          disabled={viewOnly || isAdministrator}
                          onCheckedChange={(c) => onManageRolesPermissionsChange(c as boolean)}
                        />
                      }
                    />
                  </div>
                </TooltipTrigger>
                {isAdministrator
                  ? <TooltipContent><p>This permission cannot be disabled on the Administrator role</p></TooltipContent>
                  : viewOnly
                    ? <TooltipContent><p>Requires 'Configure' access to General Settings</p></TooltipContent>
                    : null
                }
              </Tooltip>
            </TooltipProvider>

            {/* Manage Roles & Rates — standalone: independent of the General Settings Configure gate */}
            <PermissionCard
              label={<Label htmlFor="manage-rates" className="cursor-pointer">Manage Roles & Rates</Label>}
              description="Access the Roles & Rates screen to view and manage role-based billing and cost rates"
              control={
                <Checkbox
                  id="manage-rates"
                  checked={manageRates}
                  onCheckedChange={(c) => onManageRatesChange(c as boolean)}
                />
              }
            />
          </div>
        </SectionAccordionItem>
      </Accordion>
    </>
  );
};
